import { Button, Group, Text } from '@mantine/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';

// Button style configurations
const buttonStyles = {
  back: {
    variant: "light",
    color: "gray",
    size: "md",
    styles: {
      root: {
        fontWeight: 500,
      }
    }
  },
  
  primary: {
    variant: "filled",
    color: "indigo",
    size: "md",
    styles: (theme) => ({
      root: {
        fontWeight: 600,
        backgroundColor: theme.colors.indigo[5],
        '&:hover': {
          backgroundColor: theme.colors.indigo[6],
        },
        '&:disabled': {
          backgroundColor: theme.colors.gray[3],
          color: theme.colors.gray[5],
        }
      }
    })
  }
};

// Back button component
export const BackButton = ({ onClick, children = "Back", ...props }) => (
  <Button 
    {...buttonStyles.back}
    leftSection={<IconArrowLeft size={16} />}
    onClick={onClick}
    {...props}
  >
    {children}
  </Button>
);

// Continue/Next button component (with arrow)
export const ContinueButton = ({ onClick, disabled, children, ...props }) => (
  <Button 
    {...buttonStyles.primary}
    rightSection={<IconArrowRight size={16} />}
    onClick={onClick}
    disabled={disabled}
    {...props}
  >
    {children}
  </Button>
);

// Action button component (no arrow, same primary style)
export const ActionButton = ({ onClick, disabled, children, leftSection, rightSection, ...props }) => (
  <Button 
    {...buttonStyles.primary}
    onClick={onClick}
    disabled={disabled}
    leftSection={leftSection}
    rightSection={rightSection}
    {...props}
  >
    {children}
  </Button>
);

// Navigation wrapper - already handles spacing
export const NavigationWrapper = ({ children }) => (
  <Group justify="space-between" mt="xl">
    {children}
  </Group>
);

// Full Navigation component with built-in spacing
export const Navigation = ({ 
  onBack, 
  onNext, 
  nextLabel = "Continue", 
  nextDisabled = false,
  showBack = true 
}) => (
  <NavigationWrapper>
    {showBack && onBack ? <BackButton onClick={onBack} /> : <div />}
    <ContinueButton onClick={onNext} disabled={nextDisabled}>
      {nextLabel}
    </ContinueButton>
  </NavigationWrapper>
);

// Action buttons wrapper - for non-navigation actions with same spacing
export const ActionButtons = ({ 
  leftAction,
  rightAction,
  leftLabel,
  rightLabel,
  leftIcon,
  rightIcon,
  leftDisabled,
  rightDisabled,
  onLeftClick,
  onRightClick
}) => (
  <NavigationWrapper>
    {leftAction || <div />}
    {rightAction && (
      <ActionButton 
        onClick={onRightClick}
        disabled={rightDisabled}
        leftSection={rightIcon}
      >
        {rightLabel}
      </ActionButton>
    )}
  </NavigationWrapper>
);

// Stepper Navigation component - for step-by-step workflows
export const StepperNavigation = ({ 
  currentStep, 
  totalSteps, 
  onPrevious, 
  onNext,
  stepLabels = [],
  canAdvance = true,
  previousLabel,
  nextLabel,
  showStepInfo = true
}) => {
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  
  const getPreviousLabel = () => {
    if (previousLabel) return previousLabel;
    if (stepLabels[currentStep - 1]) return `← ${stepLabels[currentStep - 1]}`;
    return "← Previous";
  };
  
  const getNextLabel = () => {
    if (nextLabel) return nextLabel;
    if (stepLabels[currentStep + 1]) return `${stepLabels[currentStep + 1]} →`;
    return "Next →";
  };

  return (
    <NavigationWrapper>
      <Group spacing="md">
        {!isFirstStep ? (
          <BackButton onClick={onPrevious}>
            {getPreviousLabel()}
          </BackButton>
        ) : <div />}
        
        {showStepInfo && (
          <Text size="sm" color="dimmed" style={{ alignSelf: 'center' }}>
            Step {currentStep + 1} of {totalSteps}
          </Text>
        )}
      </Group>
      
      {!isLastStep && (
        <ContinueButton 
          onClick={onNext}
          disabled={!canAdvance}
        >
          {getNextLabel()}
        </ContinueButton>
      )}
    </NavigationWrapper>
  );
};